import React, { useState, useEffect, useRef, useCallback } from 'react';
import ProductCard from './ProductCard.jsx';

const ProductList = ({ products }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [centerProductIndex, setCenterProductIndex] = useState(null);
  const containerRef = useRef(null);
  const scrollerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const isDragging = useRef(false);
  const animationRef = useRef(null);
  const lastTapTime = useRef(0);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Add animation styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes infiniteScroll {
        0% {
          transform: translateX(0);
        }
        100% {
          transform: translateX(-50%);
        }
      }
      
      .animate-infinite-scroll {
        animation: infiniteScroll var(--scroll-duration, 60s) linear infinite;
      }
      
      .animate-paused {
        animation-play-state: paused;
      }
      
      @media (max-width: 640px) {
        .animate-infinite-scroll {
          animation-duration: var(--mobile-duration, 50s);
        }
      }

      .center-card-highlight {
        filter: none !important;
        transform: scale(1.1) !important;
        transition: all 0.3s ease;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // Set scroll duration based on number of products
    const duration = products?.length > 0 ? Math.max(products.length * 3, 8) : 8;
    document.documentElement.style.setProperty('--scroll-duration', `${duration * 5}s`);
    document.documentElement.style.setProperty('--mobile-duration', `${duration * 3.5}s`);
  }, [products]);

  // Function to find which product is in the center of the viewport
  const findCenterProduct = useCallback(() => {
    if (!isMobile || !scrollerRef.current) return;
    
    const container = scrollerRef.current;
    const cards = container.querySelectorAll('.product-card-wrapper');
    const viewportCenter = window.innerWidth / 2;
    
    let closestCard = null;
    let minDistance = Infinity;
    
    cards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const distance = Math.abs(cardCenter - viewportCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestCard = index;
      }
    });
    
    // Only update if we found a card close to center (within threshold)
    if (closestCard !== null && minDistance < 100) {
      setCenterProductIndex(closestCard % products.length); // Map to original product index
    } else {
      setCenterProductIndex(null);
    }
  }, [isMobile, products.length]);

  // Track center product during animation
  useEffect(() => {
    if (!isMobile) return;
    
    const checkInterval = setInterval(() => {
      findCenterProduct();
    }, 100); // Check every 100ms
    
    return () => clearInterval(checkInterval);
  }, [isMobile, findCenterProduct]);

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    isDragging.current = false;
    
    // Find center product on touch start
    findCenterProduct();
  };

  const handleTouchMove = (e) => {
    if (!isMobile) return;
    const deltaX = Math.abs(touchStartX.current - e.touches[0].clientX);
    const deltaY = Math.abs(touchStartY.current - e.touches[0].clientY);
    
    // If horizontal movement, pause animation
    if (deltaX > 10 && deltaX > deltaY) {
      isDragging.current = true;
      setIsPaused(true);
    }
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    const deltaX = Math.abs(touchStartX.current - e.touches[0].clientX);
    const deltaY = Math.abs(touchStartY.current - e.touches[0].clientY);
    const touchDuration = Date.now() - touchStartTime.current;
    
    // Detect tap (minimal movement and quick duration)
    if (!isDragging.current && deltaX < 10 && deltaY < 10 && touchDuration < 200) {
      // Check for double tap
      const now = Date.now();
      if (now - lastTapTime.current < 300) {
        // Double tap detected - could add special behavior
        console.log('Double tap detected');
      }
      lastTapTime.current = now;
      
      // If there's a center product, trigger its click
      if (centerProductIndex !== null && products[centerProductIndex]) {
        e.preventDefault();
        handleProductClick(products[centerProductIndex]);
      }
    }
    
    // Resume animation after a delay
    setTimeout(() => {
      setIsPaused(false);
      isDragging.current = false;
    }, 2000);
  };

  // Handle product click
  const handleProductClick = (product) => {
    if (product.website) {
      window.open(product.website, '_blank', 'noopener,noreferrer');
    } else if (product.url) {
      window.open(product.url, '_blank', 'noopener,noreferrer');
    } else {
      console.log('No URL available for:', product.name);
    }
  };

  // Desktop hover handlers - removed to prevent stopping the scroll
  // const handleMouseEnter = () => {
  //   if (!isMobile) {
  //     setIsPaused(true);
  //   }
  // };

  // const handleMouseLeave = () => {
  //   if (!isMobile) {
  //     setIsPaused(false);
  //   }
  // };

  if (!products || products.length === 0) {
    return <p className="text-center py-10">No companies found.</p>;
  }

  // Create multiple copies for seamless infinite scroll
  const duplicatedProducts = [...products, ...products, ...products, ...products];

  return (
    <div 
      className="overflow-hidden w-full [mask-image:_linear-gradient(to_right,transparent_0,_black_128px,_black_calc(100%-128px),transparent_100%)]"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Center tap indicator */}
      {isMobile && centerProductIndex !== null && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium animate-pulse">
            Tap to open {products[centerProductIndex]?.name}
          </div>
        </div>
      )}
      
      <div 
        ref={containerRef}
        className="relative overflow-hidden"
      >
        <div 
          ref={scrollerRef}
          className={`flex whitespace-nowrap pt-10 ${
            isPaused ? 'animate-paused' : 'animate-infinite-scroll'
          } ${isMobile ? 'gap-[18px]' : 'gap-[20px]'}`}
          style={{ 
            gap: isMobile ? '20px' : '22px',
            width: 'fit-content',
            willChange: 'transform',
            touchAction: isMobile ? 'pan-y' : 'auto',
          }}
        >
          {duplicatedProducts.map((product, index) => {
            const originalIndex = index % products.length;
            const isCenterCard = isMobile && centerProductIndex === originalIndex;
            
            return (
              <div 
                key={`${product.id || index}-${index}`}
                className={`product-card-wrapper flex-shrink-0 transition-all duration-300 ${
                  isCenterCard ? 'scale-110 z-20' : ''
                }`}
                style={{ 
                  pointerEvents: isMobile ? (isCenterCard ? 'auto' : 'none') : 'auto',
                  opacity: isMobile ? (isCenterCard ? 1 : 0.7) : 1,
                  filter: isMobile ? (isCenterCard ? 'none' : '') : 'none',
                }}
              >
                <ProductCard 
                  product={product} 
                  isClickable={!isMobile || isCenterCard}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductList;